import apiClient from './api';
import storage from './storage';

const authService = {
  /**
   * Login user with mobile number and password
   * @param {string} mobileNumber - User's mobile number
   * @param {string} countryCode - Country code (e.g., "91")
   * @param {string} password - User's password
   * @returns {Promise<Object>} User data and token
   */
  login: async (mobileNumber, countryCode, password) => {
    try {
      const response = await apiClient.post('/api/Auth/login', {
        mobileNumber,
        countryCode,
        password,
      });

      const { token, userId, name, surName, userType, memberId, mediatorId, bureauName } = response.data;

      // Store token securely
      if (token) {
        await storage.setItem('authToken', token);
        
        // Store user data
        const userData = {
          userId,
          name,
          surName,
          userType,
          mobileNumber,
          countryCode,
          memberId,
          mediatorId,
          bureauName,
        };
        
        await storage.setItem('userData', JSON.stringify(userData));
      }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Login failed. Please try again.',
      };
    }
  },

  /**
   * Register a new member
   * @param {Object} memberData - Registration data
   * @returns {Promise<Object>} Registration response
   */
  registerMember: async (memberData) => {
    try {
      const response = await apiClient.post('/api/Auth/register/member', memberData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.',
      };
    }
  },

  /**
   * Register a new mediator
   * @param {Object} mediatorData - Registration data
   * @returns {Promise<Object>} Registration response
   */
  registerMediator: async (mediatorData) => {
    try {
      const response = await apiClient.post('/api/Auth/register/mediator', mediatorData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Registration failed. Please try again.',
      };
    }
  },

  /**
   * Validate current token
   * @returns {Promise<boolean>} Token validity
   */
  validateToken: async () => {
    try {
      console.log('authService: validateToken called');
      const token = await storage.getItem('authToken');
      console.log('authService: token exists:', !!token);
      if (!token) return false;

      console.log('authService: calling API to validate token...');
      const response = await apiClient.post('/api/Auth/validate-token', `"${token}"`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('authService: validate token response:', response.data);
      return response.data === true;
    } catch (error) {
      console.error('authService: validateToken error:', error);
      return false;
    }
  },

  /**
   * Logout user
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      await storage.deleteItem('authToken');
      await storage.deleteItem('userData');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  },

  /**
   * Get stored user data
   * @returns {Promise<Object|null>} User data or null
   */
  getUserData: async () => {
    try {
      const userDataString = await storage.getItem('userData');
      return userDataString ? JSON.parse(userDataString) : null;
    } catch (error) {
      console.error('Error retrieving user data:', error);
      return null;
    }
  },

  /**
   * Forgot password
   * @param {string} mobileNumber - User's mobile number
   * @param {string} countryCode - Country code
   * @returns {Promise<Object>} Response with temporary password
   */
  forgotPassword: async (mobileNumber, countryCode) => {
    try {
      const response = await apiClient.post('/api/Auth/forgot-password', {
        mobileNumber,
        countryCode,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to reset password. Please try again.',
      };
    }
  },

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @param {string} confirmPassword - Confirm new password
   * @returns {Promise<Object>} Response
   */
  changePassword: async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await apiClient.post('/api/Auth/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to change password. Please try again.',
      };
    }
  },
};

export default authService;
